import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Router } from '@angular/router';
import { ApiService } from './api.service';
import { ENDPOINTS } from 'src/app/_configs/endpoints';
import { PARAMS } from 'src/app/_configs/params';
import { environment } from 'src/environments/environment';
import { catchError, map, timeout } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

@Injectable({
  providedIn: 'root'
})

export class SharedService {

  isServer: boolean = false;
  isBrowser: boolean = true;
  http: any;

  constructor(private apiService: ApiService,
    private router: Router,
    @Inject(PLATFORM_ID) platformId: any) {
    this.isServer = isPlatformServer(platformId);
    this.isBrowser = isPlatformBrowser(platformId);
  }

  retrieveKeywordListTendersNew(): Observable<any> {
    const url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.TENDER_SUMMARY_BY_KEYWORD}`;
    return this.apiService.get(url).pipe(
      timeout(3000), // Timeout after 3000 milliseconds
      catchError(error => {
        console.error('Error retrieving keyword list tenders:', error);
        return of([]); // Return empty array or appropriate default value
      })
    );
  }

  retrieveAuthorityListTendersNew(): Observable<any> {
    const url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.TENDER_SUMMARY_BY_AUTHORITY}`;
    return this.apiService.get(url).pipe(
      timeout(3000), // Timeout after 3000 milliseconds
      catchError(error => {
        console.error('Error retrieving authority list tenders:', error);
        return of([]); // Return empty array or appropriate default value
      })
    );
  }

  retrieveSeoDetails(type: any, params: any, secondParams: any): Observable<any> {
    let url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.RETRIEVE_SEO_DETAILS}?`;
    if (secondParams) {
      url += `state=${params}&`;
      url += `${type}=${secondParams}`;
    } else {
      url += `${type}=${params}`;
    }
    return this.apiService.get(url).pipe(catchError(error => {
      console.error('Error retrieving SEO details:', error);
      return of(null); // Return null or an appropriate default value
    })
    );
  }

  subscribeTenders(requestData: any): Observable<any> {
    const url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.SUBSCRIBE_TENDER}`;
    return this.apiService.post(url, requestData).pipe(
      timeout(3000), // Timeout after 3000 milliseconds
      catchError(error => {
        console.error('Error subscribing to tenders:', error);
        return of(null); // Return null or an appropriate default value
      })
    );
  }

  retrieveFaqDetails(entityId: any, urlSegments: any): Observable<any> {
    if (urlSegments.length > 2) {
      const type = urlSegments[1];
      const baseUrl = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.FAQLIST}?entityId=${entityId}`;

      // Handle 'city' case with both city and state
      const url = (type === 'city' && urlSegments.length > 3)
        ? `${baseUrl}&${type}=${urlSegments[3]}&state=${urlSegments[2]}`
        : `${baseUrl}&${type}=${urlSegments[2]}`;

      // Return the API call with error handling
      return this.apiService.get(url).pipe(
        catchError(error => {
          console.error('Error retrieving FAQ details:', error);
          return of(null); // Return null or a default value on error
        })
      );
    } else {
      const url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.FAQLIST}?entityId=${entityId}`;
      return this.apiService.get(url).pipe(catchError(error => {
        console.error('Error retrieving tenders by cities:', error);
        return of({}); // Return empty array or appropriate default value
      })
      );
    }
    // Return null if urlSegments is invalid
    return of(null);
  }

  retrieveTenderByCitiesNew(): Observable<any> {
    const url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.RETRIEVE_TENDER_BY_CITIES}`;
    return this.apiService.get(url).pipe(catchError(error => {
      console.error('Error retrieving tenders by cities:', error);
      return of({}); // Return empty array or appropriate default value
    })
    );
  }

  contactUs(contactUs: any) {
    let url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.CONTACT_US}`
    return this.apiService.post(url, contactUs);
  }

  otpSend(mobileNumber: any) {
    let url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.OTP.OTP_SEND}?phone=${mobileNumber}`;
    return this.apiService.post(url, mobileNumber);
  }

  otpVerify(requestJson: any) {
    let url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.OTP.OPT_VERIFY}`;
    return this.apiService.post(url, requestJson);
  }

  otpSignup(requestJson: any) {
    let url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.OTP.OTP_SIGNUP}`;
    return this.apiService.post(url, requestJson);
  }

  appDownloadLink(mobileNumber: any) {
    let url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.APP_DOWNLOAD}?phone=${mobileNumber}`;
    return this.apiService.post(url, mobileNumber);
  }

  setPreference(requestJson: any) {
    let url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.SET_PREFERENCES}`;
    return this.apiService.post(url, requestJson);
  }

  userinterest(requestJson: any) {
    let url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.USER_INTEREST}`;
    return this.apiService.post(url, requestJson);
  }

  // interestPlan(id: any, token: any, plan: any) {
  //   let url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.INTEREST_PLAN}`;
  //   return this.apiService.put(url);
  // }

  interestPlan(id: string, token: string, planSelected: string) {
    const baseUrl = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.INTEREST_PLAN}`;
    const url = `${baseUrl}?id=${id}&planSelected=${planSelected}&token=${token}`;
    const payload = {
      id: id,
      token: token,
      plan: planSelected,
    };
    return this.apiService.put(url, payload);
  }

  downloadFile(fileUrl: string) {
    let url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.DOWNLOAD_DOC}?url=${fileUrl}&download=1`;
    return this.apiService.get(url, { responseType: 'blob' });
  }

  verifySearchBarShowHideCond() {
    if (this.router.url.split('?')[0] !== PARAMS.ROUTING_PATHS.HOME && this.router.url.split('?')[0] !== PARAMS.ROUTING_PATHS.ABOUT_US && this.router.url.split('?')[0] !== PARAMS.ROUTING_PATHS.LANDING_PAGE) {
      return true
    }
    else {
      return false
    }
  }

  retrievetrending(type: any, value: any) {
    let url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.RETRIEVE_TRENDING}?${type}=${value}`
    return this.apiService.get(url)
  }

  retrievetrendingSearches(type: any, value: any) {
    let url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.TRENDING_SEARCH}?${type}=${value}`
    return this.apiService.get(url)
  }


  tenderDetailsPageUrl(tender: any): string {
    return `/details/${tender?.state?.link}-tender/${tender?.authority?.name}/${tender?.id}`;
  }

  getCurrentPageUrl() {
    return this.router['location']._platformLocation.location;
  }

  getBlogList(pageSize: any, pageNumber: any, slug: any) {
    let url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.BLOG_LIST}?pageNumber=${pageNumber}&postSize=${pageSize}&slug=${slug}`;
    return this.apiService.get(url);
  }

  getNewlyTenders() {
    let url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.NEWLY_ADDED_TENDERS}`;
    return this.apiService.get(url);
  }

  retrieveStateListTenders() {
    let url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.TENDER_SUMMARY_BY_STATE}`;
    return this.apiService.get(url);
  }

  getBlogDetails(slug: any) {
    const url = `${environment.TENDER.BLOG_BASE_URL}${ENDPOINTS.TENDER.BLOG.BLOG_Details}?_embed&slug=${slug}`;
    return this.apiService.get(url).pipe(
      map((response: any) => {
        const blog = response[0];
        if (!blog) {
          throw new Error('Blog not found');
        }
        // Check if `_embedded` and `wp:featuredmedia` exist and have at least one item
        const featureImage = blog._embedded && blog._embedded['wp:featuredmedia']
          ? blog._embedded['wp:featuredmedia'][0]?.source_url || '' : '';
        return {
          id: blog.id,
          title: blog.title?.rendered || 'No Title',
          content: blog.content?.rendered || 'No Content',
          date: new Date(blog.date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }),
          slug: blog.slug || '',
          featureImage: featureImage
        };
      })
    );
  }

  guestIdentify(guestId: any) {
    let url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.GUESTIDENTIFY}?guestId=${guestId}`;
    return this.apiService.post(url, guestId);
  }

  planInterest(userid: any, selectedPlan: any) {
    let url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.PLANINTEREST}?planSelected=${selectedPlan}&userId=${userid}`;
    return this.apiService.post(url, userid);
  }

  listingExport(payload: any) {
    const url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CSV_EXPORT}`;
    return this.apiService.post(url, payload, { responseType: 'blob' });
  }

  retrieveValidDate(date: any): string {
    const parseDate = (date: any): Date | null => {
      if (typeof date === 'string') {
        const parsedDate = new Date(date);
        return !isNaN(parsedDate.getTime()) ? parsedDate : null;
      } else if (date instanceof Date) {
        return !isNaN(date.getTime()) ? date : null;
      } else if (typeof date === 'number') {
        const parsedDate = new Date(date);
        return !isNaN(parsedDate.getTime()) ? parsedDate : null;
      }
      return null;
    };

    const parsedDate = parseDate(date);
    return parsedDate ? parsedDate.toISOString().split('T')[0] : '';
  }

  // retrieveValidDate(date: any): string {
  //   const parseDate = (date: any): Date | null => {
  //     if (typeof date === 'string') {
  //       const parsedDate = parseISO(date);
  //       return isValid(parsedDate) ? parsedDate : null;
  //     } else if (date instanceof Date) {
  //       return date;
  //     } else if (typeof date === 'number') {
  //       const parsedDate = new Date(date);
  //       return isValid(parsedDate) ? parsedDate : null;
  //     }
  //     return null;
  //   };

  //   const parsedDate = parseDate(date);
  //   return parsedDate ? format(parsedDate, 'yyyy-MM-dd') : '';
  // }

  capitalizeTheFirstLetterOfEachWord(type: string) {
    var typeWithoutUnderscore = type.replace(PARAMS.REGEX.UNDERSOCRE_REPLACE_WITH_SPACE_REGEX, " ");
    var separateWord = typeWithoutUnderscore.toLowerCase().split(' ');

    for (var i = 0; i < separateWord?.length; i++) {
      separateWord[i] = separateWord[i].charAt(0).toUpperCase() +
        separateWord[i].substring(1);
    }

    return separateWord.join(' ');
  }

  homepageNavigationFixedOnMobileView() {
    if (this.isBrowser) {
      let elements = document.getElementById("main_header") as HTMLElement;

      function fixedviewOnScroll(_verticalScrollP: number, _className: string, _elementnName: Element) {
        if (window?.scrollY > _verticalScrollP) {
          _elementnName?.classList?.add(_className);
        }
        else {
          _elementnName?.classList?.remove(_className);
        }
      }

      if (window?.outerWidth < 576) {
        (function fixedheaderonscroll() {
          fixedviewOnScroll(30, "fixed", elements)
        })();
      }
    }
  }

  tenderDetailsPageNavigationFixedOnMobileView(numberOfDocs: number) {
    if (this.isBrowser) {
      let elements = document.getElementById("navTabshorizontal") as HTMLElement;

      function FixedviewOnScroll(_verticalScrollP: number, _className: string, _elementnName: Element) {
        if (window?.scrollY > _verticalScrollP) {
          _elementnName?.classList?.add(_className);
        }
        else {
          _elementnName?.classList?.remove(_className);
        }
      }

      if (numberOfDocs > 0) {
        FixedviewOnScroll(300, "navtabs-fixed", elements)
      }
    }
  }

  onScroll(event: Event) {
    let data = { isRightVisible: true, isLeftVisible: true };
    let val = (event.target as HTMLElement).scrollLeft;
    let maxScrollLeft = (event.target as HTMLElement).scrollWidth - (event.target as HTMLElement).clientWidth;
    if (val == 0) data.isLeftVisible = false
    else {
      data.isLeftVisible = true;
    }
    if (val == maxScrollLeft) data.isRightVisible = false;
    else data.isRightVisible = true;
    return data;
  }
  scrollDiv(containerId: string, numberOfItems: number, right: boolean) {
    const element: any = document.getElementById(containerId);
    const scrollAmount = element.firstChild.offsetWidth;
    right ? element.scrollLeft += scrollAmount : element.scrollLeft -= scrollAmount
  }
}

export interface ServerLogSchema {
  logId?: any,
  startDateTime: any;
  endDateTime: any;
  startDateTimeV2?: any;
  endDateTimev2?: any;
  apiRequestTime?: any,
  apiURL: string;
  method: string;
  payload: any;
  responseStatus: any;
  sessionId: any;
}